import type { RelationOverride } from '@/systems/RelationResolver';
import type { AbilityUnlockRule } from '@/systems/AbilitySystem';
import type { BrainQuality } from '@/systems/BrainSystem';
import type { SpeciesDef } from '@/entities/species';

/**
 * Data-driven arc description. An arc is a content package — species,
 * tech-tree contributions, named characters, entry scene, narrative
 * beats, locale strings. Adding an arc is a manifest registration,
 * never an engine change.
 *
 * LocaleKey is a string until the i18n system lands (then a branded
 * type).
 */
export type LocaleKey = string;
export type ArcId = string;
export type SceneKey = string;

export interface UnlockCondition {
  type: 'always' | 'arc-completed' | 'dlc-owned' | 'alignment-reached';
  value?: string;
}

export interface ArcManifest {
  id: ArcId;
  displayName: LocaleKey;
  description?: LocaleKey;
  entryScene: SceneKey;
  unlockedBy?: UnlockCondition;
  species?: Record<string, SpeciesDef>;
  relationOverrides?: RelationOverride[];
  abilityUnlockRules?: AbilityUnlockRule[];
  brainQualityFor?: (speciesId: string) => BrainQuality | undefined;
  localeBundle?: Record<LocaleKey, string>;
}

export class ArcRegistry {
  private readonly manifests = new Map<ArcId, ArcManifest>();

  register(manifest: ArcManifest): void {
    if (this.manifests.has(manifest.id)) {
      throw new Error(`Arc "${manifest.id}" already registered`);
    }
    this.manifests.set(manifest.id, manifest);
  }

  get(id: ArcId): ArcManifest | undefined {
    return this.manifests.get(id);
  }

  all(): readonly ArcManifest[] {
    return Array.from(this.manifests.values());
  }

  isUnlocked(
    id: ArcId,
    context: {
      completedArcs: ReadonlySet<ArcId>;
      ownedDLCs: ReadonlySet<string>;
      alignment?: string;
    },
  ): boolean {
    const manifest = this.manifests.get(id);
    if (!manifest) return false;
    const cond = manifest.unlockedBy ?? { type: 'always' };
    switch (cond.type) {
      case 'always':
        return true;
      case 'arc-completed':
        return !!cond.value && context.completedArcs.has(cond.value);
      case 'dlc-owned':
        return !!cond.value && context.ownedDLCs.has(cond.value);
      case 'alignment-reached':
        return !!cond.value && context.alignment === cond.value;
    }
  }

  clear(): void {
    this.manifests.clear();
  }
}
